import { useTranslation } from 'react-i18next';

/** @graph-connects none */
const SLACK_WORKSPACE_HOST = 'air-closet.slack.com';

interface SlackChannelButtonProps {
  /** PJ Slack channel ID（C... 形式）。優先 */
  pjChannelId: string | null;
  /** 開発 Slack channel ID（C... 形式）。フォールバック */
  devChannelId: string | null;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板の Slack channel ボタン。`pj_slack_channel_id` 優先で対応する Slack workspace の URL を新規タブで開く。両方未設定なら disabled
 * @graph-connects none
 */
export function SlackChannelButton({ pjChannelId, devChannelId }: SlackChannelButtonProps) {
  const { t } = useTranslation();
  const channelId = pjChannelId ?? devChannelId;
  const disabled = !channelId;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!channelId) return;
    const url = `https://${SLACK_WORKSPACE_HOST}/archives/${channelId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={
        disabled ? t('pmoBoard.slackUnavailable') : t('pmoBoard.openSlack', { id: channelId ?? '' })
      }
      aria-label={t('pmoBoard.openSlack', { id: channelId ?? '' })}
      className={`
        inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium
        ${
          disabled
            ? 'text-[var(--color-text-muted)] cursor-not-allowed'
            : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
        }
      `}
    >
      <SlackIcon className="w-4 h-4" />
      <span>Slack</span>
    </button>
  );
}

/** @graph-connects none */
function SlackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  );
}
